import React, { useRef, useState, useEffect, useCallback } from "react";
import RouteGroupCard from "../scheduling/RouteGroupCard";

const Node = ({
  id,
  x,
  y,
  onMove,
  onSelect,
  date,
  onDoubleClick,
  isSelected,
  title,
  routes,
  fetchRouteGroupsAndConnections,
  color,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const nodeRef = useRef(null);
  const pointerIdRef = useRef(null);

  const handlePointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;

    const interactiveTarget = e.target?.closest?.(
      "button, input, textarea, select, a, [role='button'], [data-no-drag='true']",
    );
    if (interactiveTarget) return;

    e.preventDefault();
    setIsDragging(true);
    onSelect(id);
    pointerIdRef.current = e.pointerId;

    const rect = nodeRef.current?.getBoundingClientRect();
    if (rect) {
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
    }
    nodeRef.current.style.zIndex = 9999;
  };

  const handlePointerMove = useCallback(
    (e) => {
      if (!isDragging) return;
      if (pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current)
        return;

      const container = nodeRef.current?.parentElement;
      if (!container) return;

      const containerRect = container.getBoundingClientRect();
      const newX = e.clientX - containerRect.left - dragOffset.x;
      const newY = e.clientY - containerRect.top - dragOffset.y;

      const constrainedX = Math.max(
        0,
        Math.min(newX, containerRect.width - 50),
      );
      const constrainedY = Math.max(
        0,
        Math.min(newY, containerRect.height - 50),
      );

      onMove(id, constrainedX, constrainedY);
    },
    [isDragging, dragOffset, onMove, id],
  );

  const handlePointerUp = useCallback((e) => {
    if (pointerIdRef.current !== null && e.pointerId !== pointerIdRef.current)
      return;
    setIsDragging(false);
    pointerIdRef.current = null;
    nodeRef.current.style.zIndex = 1050;
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
      document.addEventListener("pointercancel", handlePointerUp);
      return () => {
        document.removeEventListener("pointermove", handlePointerMove);
        document.removeEventListener("pointerup", handlePointerUp);
        document.removeEventListener("pointercancel", handlePointerUp);
      };
    }
  }, [isDragging, dragOffset, handlePointerMove, handlePointerUp]);

  const handleDoubleClick = () => {
    onDoubleClick(id);
  };

  const calculatedPassengers = routes.flatMap(
    (route) => route.passengers || [],
  );

  return (
    <div
      ref={nodeRef}
      className={`d-flex align-items-center justify-content-center text-white fw-bold small ${
        isDragging ? "scale-110 shadow-lg" : ""
      }`}
      style={{
        position: "absolute",
        left: `${x}px`,
        top: `${y}px`,
        userSelect: "none",
        transform: isDragging ? "scale(1.1) rotate(2deg)" : "none",
        zIndex: 1050,
        transition: "all 0.2s ease",
        cursor: "move",
        padding: "12px",
        pointerEvents: "auto",
        touchAction: "none",
      }}
      onPointerDown={handlePointerDown}
      onDoubleClick={handleDoubleClick}
    >
      <RouteGroupCard
        title={title}
        routeGroupId={id}
        calculatedPassengers={calculatedPassengers}
        routes={routes}
        fromOrganization={true}
        refetch={fetchRouteGroupsAndConnections}
        isNode={true}
        date={date}
        color={color}
      />
    </div>
  );
};

export default Node;
